/** Tenant entitlement display; the server authorizes every command independently. */

import { useEffect, useState } from "react";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";
import { restGet } from "./restClient.js";

/** Entitlement keys, matching `shared_db::models`. */
export const ENTITLEMENTS = {
    ABAC: "abac",
    AI_VISIBILITY: "ai_visibility",
    ARTIFACT_ROLLOUT_CONTROL: "artifact_rollout_control",
    DISABLE_AUTO_UPDATE: "disable_auto_update",
    MANAGED_LLM_CHAT: "managed_llm_chat",
};

/** Entitled surfaces stay hidden until the tenant entitlement is confirmed. */
export function hasEntitlement(entitlements, key) {
    return entitlements?.includes(key) === true;
}

let _read = null;
let _keys = null;
onAuthIdentityChange(() => {
    _read = null;
    _keys = null;
});

function readEntitlements() {
    if (_read) return _read;
    const epoch = getAuthIdentityEpoch();
    const read = restGet("/v1/tenant/configuration").then((response) => {
        // An absent field is an older backend; an empty array is a
        // tenant that genuinely holds nothing. The gateway emits
        // unpopulated fields, so the two are distinguishable here.
        const keys = response?.entitlements;
        if (getAuthIdentityEpoch() !== epoch || !Array.isArray(keys)) return null;
        if (_read === read) _keys = keys;
        return keys;
    }).catch(() => {
        if (_read === read) _read = null;
        return null;
    });
    _read = read;
    return read;
}

/** Return the tenant's entitlement keys, or null while unavailable. */
export function useTenantEntitlements() {
    const [entitlements, setEntitlements] = useState(() => _keys);

    useEffect(() => {
        let active = true;
        readEntitlements().then((keys) => {
            if (active && keys) setEntitlements(keys);
        });
        return () => {
            active = false;
        };
    }, []);

    return entitlements;
}

export function _resetEntitlementsForTests() {
    _read = null;
    _keys = null;
}
